/**
 * SeatInventoryLayer.jsx - Renders seat inventory with assignment states
 * Elvis Aloha Collection
 * 
 * Displays persistent seat geometries with color-coded assignment states:
 * - Light blue: Unassigned (seat exists, no ticket)
 * - Green: Verified assignment (published ticket)
 * - Orange: Pending assignment (under review)
 * - Gray: Unavailable
 * - Gold outline: Selected
 * 
 * Clicking unassigned seats opens assignment modal for owner.
 * Clicking assigned seats opens ticket details.
 */

import React, { useState, useEffect, useCallback } from 'react';

// Display state colors
const STATE_COLORS = {
  unassigned: {
    fill: 'rgba(147, 197, 253, 0.6)',  // Light blue
    stroke: '#3B82F6',
    hoverFill: 'rgba(147, 197, 253, 0.8)'
  },
  assigned_verified: {
    fill: 'rgba(134, 239, 172, 0.7)',  // Green
    stroke: '#22C55E',
    hoverFill: 'rgba(134, 239, 172, 0.9)'
  },
  assigned_pending: {
    fill: 'rgba(253, 186, 116, 0.7)',  // Orange
    stroke: '#F97316',
    hoverFill: 'rgba(253, 186, 116, 0.9)'
  },
  unavailable: {
    fill: 'rgba(156, 163, 175, 0.5)',  // Gray
    stroke: '#6B7280',
    hoverFill: 'rgba(156, 163, 175, 0.6)'
  },
  selected: {
    stroke: '#EAB308',  // Gold outline
    strokeWidth: 3
  }
};

const SeatInventoryLayer = ({
  seats = [],
  template,
  transform,
  selectedSeatId,
  onSelectSeat,
  onOpenAssignModal,
  onOpenTicketDetails,
  hoveredSeatId,
  onHoverSeat,
  isOwner = false,
  showLabels = true
}) => {
  if (!transform || seats.length === 0) return null;

  return (
    <g className="seat-inventory-layer" data-testid="seat-inventory-layer">
      {seats.map(seat => {
        const width = (seat.width || 17) * (transform.scale || 1);
        const height = (seat.height || 14) * (transform.scale || 1);
        
        // Calculate screen position from native coords
        const screenX = (seat.native_x || 0) * transform.scale + transform.translateX;
        const screenY = (seat.native_y || 0) * transform.scale + transform.translateY;
        
        // Top-left corner (native position is center)
        const tlX = screenX - width / 2;
        const tlY = screenY - height / 2;
        
        const isSelected = seat.id === selectedSeatId;
        const isHovered = seat.id === hoveredSeatId;
        const displayState = seat.display_state || 'unassigned';
        
        // Get colors based on state
        const stateColors = STATE_COLORS[displayState] || STATE_COLORS.unassigned;
        
        let fillColor = isHovered ? stateColors.hoverFill : stateColors.fill;
        let strokeColor = stateColors.stroke;
        let strokeWidth = 1.5;
        
        // Selected state - gold outline
        if (isSelected) {
          strokeColor = STATE_COLORS.selected.stroke;
          strokeWidth = STATE_COLORS.selected.strokeWidth;
        }
        
        // Handle click based on assignment status
        const handleClick = (e) => {
          e.stopPropagation();
          onSelectSeat?.(seat.id);
          
          if (isOwner) {
            if (seat.is_assigned && seat.ticket_id) {
              // Open ticket details
              onOpenTicketDetails?.(seat);
            } else {
              // Open assignment modal
              onOpenAssignModal?.(seat);
            }
          } else if (seat.is_assigned && seat.ticket_id) {
            // Public user - open ticket details
            onOpenTicketDetails?.(seat);
          }
        };

        return (
          <g 
            key={seat.id}
            className={`inventory-seat inventory-seat--${displayState} ${isSelected ? 'inventory-seat--selected' : ''}`}
            transform={`rotate(${seat.rotation || 0}, ${screenX}, ${screenY})`}
            onClick={handleClick}
            onMouseEnter={() => onHoverSeat?.(seat.id)}
            onMouseLeave={() => onHoverSeat?.(null)}
            style={{ cursor: seat.is_assigned || isOwner ? 'pointer' : 'default' }}
            data-testid={`inventory-seat-${seat.id}`}
            data-seat-state={displayState}
            role="button"
            tabIndex={0}
            aria-label={seat.accessible_label || `Section ${seat.section}, Row ${seat.row}, Seat ${seat.seat}, ${displayState}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick(e);
              }
            }}
          >
            {/* Seat shape */}
            <rect
              x={tlX}
              y={tlY}
              width={width}
              height={height}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              rx={1}
            />
            
            {/* Assignment indicator icon */}
            {displayState === 'assigned_verified' && (
              <circle
                cx={tlX + width - 4}
                cy={tlY + 4}
                r={3}
                fill="#22C55E"
                stroke="#fff"
                strokeWidth={0.5}
              />
            )}
            {displayState === 'assigned_pending' && (
              <circle
                cx={tlX + width - 4}
                cy={tlY + 4}
                r={3}
                fill="#F97316"
                stroke="#fff"
                strokeWidth={0.5}
              />
            )}
            
            {/* Selection handles when selected */}
            {isSelected && (
              <>
                {[
                  { x: tlX, y: tlY },
                  { x: tlX + width, y: tlY },
                  { x: tlX, y: tlY + height },
                  { x: tlX + width, y: tlY + height }
                ].map((pos, i) => (
                  <rect
                    key={i}
                    x={pos.x - 4}
                    y={pos.y - 4}
                    width={8}
                    height={8}
                    fill="#fff"
                    stroke="#EAB308"
                    strokeWidth={1}
                  />
                ))}
              </>
            )}
            
            {/* Tooltip on hover */}
            {(isSelected || isHovered) && showLabels && (
              <g className="seat-tooltip" style={{ pointerEvents: 'none' }}>
                <rect
                  x={screenX - 45}
                  y={tlY - 28}
                  width={90}
                  height={22}
                  fill="rgba(0,0,0,0.85)"
                  rx={4}
                />
                <text
                  x={screenX}
                  y={tlY - 13}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#fff"
                  fontWeight="500"
                >
                  {seat.section} R{seat.row} S{seat.seat}
                </text>
                {seat.ticket_reference && (
                  <text
                    x={screenX}
                    y={tlY - 2}
                    textAnchor="middle"
                    fontSize={8}
                    fill="#9CA3AF"
                  >
                    {seat.ticket_reference}
                  </text>
                )}
              </g>
            )}
          </g>
        );
      })}
    </g>
  );
};

/**
 * SeatAssignmentModal - Modal for assigning tickets to seats
 */
export const SeatAssignmentModal = ({
  isOpen,
  onClose,
  seat,
  section,
  unassignedTickets = [],
  onAssign,
  onCreateNewTicket,
  isLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [filterRow, setFilterRow] = useState('');
  
  useEffect(() => {
    if (isOpen && seat) {
      setSearchTerm('');
      setSelectedTicketId(null);
      setFilterRow(seat.row || '');
    }
  }, [isOpen, seat]);
  
  if (!isOpen || !seat) return null;
  
  // Filter tickets
  const filteredTickets = unassignedTickets.filter(ticket => {
    const matchesSearch = !searchTerm || 
      (ticket.public_reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ticket.registry_reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ticket.notes || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRow = !filterRow || String(ticket.row) === String(filterRow);
    
    return matchesSearch && matchesRow;
  });
  
  // Sort by row and seat
  const sortedTickets = [...filteredTickets].sort((a, b) => {
    const rowA = parseInt(a.row) || 999;
    const rowB = parseInt(b.row) || 999;
    if (rowA !== rowB) return rowA - rowB;
    const seatA = parseInt(a.seat) || 999;
    const seatB = parseInt(b.seat) || 999;
    return seatA - seatB;
  });
  
  const selectedTicket = sortedTickets.find(t => t.id === selectedTicketId);
  
  const handleAssign = () => {
    if (selectedTicketId && seat) {
      onAssign({
        seat_geometry_id: seat.id,
        ticket_id: selectedTicketId,
        ticket_public_reference: selectedTicket?.public_reference
      });
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="seat-assignment-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Assign Ticket to Seat
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {seat.section} Row {seat.row} Seat {seat.seat}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="close-assignment-modal"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Search and Filters */}
        <div className="px-6 py-3 border-b border-gray-100 space-y-3">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search by EAC-TKT reference, title, or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="ticket-search-input"
              />
              <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <select
              value={filterRow}
              onChange={(e) => setFilterRow(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Rows</option>
              {[1,2,3,4,5,6,7,8,9,10].map(r => (
                <option key={r} value={r}>Row {r}</option>
              ))}
            </select>
          </div>
          
          <div className="text-xs text-gray-500">
            {sortedTickets.length} unassigned ticket{sortedTickets.length !== 1 ? 's' : ''} available
          </div>
        </div>
        
        {/* Ticket List */}
        <div className="flex-1 overflow-y-auto px-6 py-3 min-h-[200px] max-h-[400px]">
          {sortedTickets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No unassigned tickets found</p>
              <button
                onClick={onCreateNewTicket}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                Create New Ticket
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedTickets.map(ticket => (
                <div
                  key={ticket.id}
                  onClick={() => setSelectedTicketId(ticket.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedTicketId === ticket.id
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`ticket-option-${ticket.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-gray-900">
                        {ticket.public_reference || ticket.registry_reference || ticket.id}
                      </span>
                      <span className="ml-2 text-sm text-gray-500">
                        Row {ticket.row} Seat {ticket.seat}
                      </span>
                    </div>
                    {selectedTicketId === ticket.id && (
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  {ticket.notes && (
                    <p className="mt-1 text-xs text-gray-500 truncate">{ticket.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            Cancel
          </button>
          <div className="flex gap-2">
            <button
              onClick={onCreateNewTicket}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-sm"
            >
              Create New Ticket
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedTicketId || isLoading}
              className={`px-6 py-2 rounded-lg text-white font-medium transition-colors ${
                selectedTicketId && !isLoading
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
              data-testid="confirm-assignment-btn"
            >
              {isLoading ? 'Assigning...' : 'Assign Ticket'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * SeatInventoryStats - Displays seat inventory statistics
 */
export const SeatInventoryStats = ({ stats }) => {
  if (!stats) return null;
  
  return (
    <div className="flex items-center gap-4 text-sm" data-testid="seat-inventory-stats">
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-full bg-blue-400 border border-blue-500"></span>
        <span className="text-gray-600">{stats.unassigned_count} Unassigned</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-full bg-green-400 border border-green-500"></span>
        <span className="text-gray-600">{stats.assigned_verified_count} Verified</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-full bg-orange-400 border border-orange-500"></span>
        <span className="text-gray-600">{stats.assigned_pending_count} Pending</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-gray-700">{stats.total_seats}</span>
        <span className="text-gray-500">Total Seats</span>
      </div>
    </div>
  );
};

export default SeatInventoryLayer;
